import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Box, Button, Divider, Typography } from '@mui/material';
import {
  ArrowCheckIcon,
  LockIcon,
  LogOutIcon,
  UserAddIcon,
  UserIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { PATH_PROFILE } from 'app/configs/routesConfig/profilePage';
import accountsJwtService from 'app/services/jwtService';
import UserAvatar from 'app/shared/components/common/UserAvatar';
import MenuPopoverCDS from 'app/shared/componentsRestyled/coreDesignSystem/MenuPopoverCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PATH_LOGIN } from 'constants/authPaths';
import { IconDirection } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';
import { getDefaultErrorMessage } from 'utils/axios';

import { logoutMasterAction } from 'store/auth/actions';
import { getMaster } from 'store/auth/selectors';
import { showErrorMessageAction } from 'store/global/actions';

import { buttonStyles, flatMenuStyles } from './MasterMenu.styles';
import { MasterMenuTestIds } from './MasterMenu.testIds';
import { getRoleNamesToDisplay } from './MasterMenu.utils';

const slotProps = { paper: { className: '!max-w-400' } };

const MasterMenu = ({ isFlat = false, onCloseSidebar }) => {
  const dispatch = useDispatch();

  const master = useSelector(getMaster);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const { displayName, firstName, impersonateMode } = master.data;
  const hasMasterRole = !_.isNull(master.role);

  const roleNamesToDisplay = useMemo(() => getRoleNamesToDisplay(master.role), [master.role]);

  const onLogoutClick = useCallback(
    e => {
      if (!impersonateMode) {
        dispatch(logoutMasterAction({ isExplicitLogout: true }));

        return onClearAnchorElement(e);
      }

      accountsJwtService.revertImpersonate().catch(err =>
        dispatch(
          showErrorMessageAction({
            message: getDefaultErrorMessage(err),
            err,
          }),
        ),
      );

      onClearAnchorElement(e);
    },
    [dispatch, impersonateMode, onClearAnchorElement],
  );

  const menuItems = useMemo(
    () =>
      hasMasterRole
        ? [
            {
              text: (
                <>
                  <Box className="!mb-8 flex flex-col">
                    <Typography variant="medium14">
                      {impersonateMode ? 'Logged in as ' : ''}
                      {displayName}
                    </Typography>

                    <Typography variant="regular14">{roleNamesToDisplay}</Typography>
                  </Box>

                  <Divider />
                </>
              ),
              className: 'pointer-events-none',
              component: Box,
              role: 'group',
              type: undefined,
              tabIndex: -1,
            },
            {
              text: impersonateMode ? `${firstName}'s Account` : 'My account',
              icon: <UserIcon />,
              component: Link,
              to: PATH_PROFILE,
            },
            {
              text: impersonateMode ? 'Get back to admin' : 'Logout',
              icon: <LogOutIcon />,
              onClick: onLogoutClick,
            },
          ]
        : [
            {
              text: 'Login',
              icon: <LockIcon />,
              component: Link,
              to: PATH_LOGIN,
            },
            {
              text: 'Register',
              icon: <UserAddIcon />,
              component: Link,
              to: '/register',
            },
          ],
    [hasMasterRole, impersonateMode, displayName, firstName, roleNamesToDisplay, onLogoutClick],
  );

  if (isFlat) {
    return (
      <Box sx={flatMenuStyles}>
        {hasMasterRole ? (
          <>
            <Link
              className="mb-8 flex items-center gap-8 flex-wrap"
              data-testid={MasterMenuTestIds.PROFILE_LINK}
              to={PATH_PROFILE}
              onClick={onCloseSidebar}
            >
              <UserAvatar withBorder size={34} />

              <Typography variant="medium14">
                {impersonateMode ? 'Logged in as ' : ''}
                {displayName}
              </Typography>
            </Link>

            <Typography variant="regular14">{roleNamesToDisplay}</Typography>
          </>
        ) : (
          <>
            <Link
              className="mb-2 block"
              data-testid={MasterMenuTestIds.LOGIN_LINK}
              to={PATH_LOGIN}
              onClick={onCloseSidebar}
            >
              Login
            </Link>

            <Link
              className="mb-2 block"
              data-testid={MasterMenuTestIds.REGISTER_LINK}
              to="/register"
              onClick={onCloseSidebar}
            >
              Register
            </Link>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <Button
        data-is-active={!!anchorEl}
        data-testid={MasterMenuTestIds.MENU_BUTTON}
        endIcon={
          <ArrowCheckIcon
            color="gray.extraSoft"
            direction={anchorEl ? IconDirection.UP : IconDirection.DOWN}
          />
        }
        sx={buttonStyles}
        onClick={onSetAnchorElement}
      >
        <UserAvatar size={34} />
      </Button>

      <MenuPopoverCDS
        anchorEl={anchorEl}
        items={menuItems}
        slotProps={slotProps}
        onClose={onClearAnchorElement}
      />
    </>
  );
};

MasterMenu.propTypes = {
  isFlat: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default MasterMenu;
